import {
  TableCellLayout,
  TableColumnSizingOptions,
  TableSelectionCell,
  createTableColumn,
} from "@fluentui/react-components";
import { columnNames } from "library/constants";
import { EmployeeRowItem } from "library/types";
import { formatDateString } from "library/utils";
import { EmployeeActions } from "./EmployeeActions/EmployeeActions";
import { EmployeeWithDependentsCell } from "./EmployeeWithDependentsCell/EmployeeWithDependentsCell";
import { TFunction } from "i18next";

type IHeaderItem =
  | {
      toggleAllRows: (e: any) => void;
      allRowsSelected: boolean;
      selectionMode: "single" | "multiselect";
      someRowsSelected: boolean;
      toggleAllKeydown: (e: any) => void;
      updateAllState: (e: any) => void;
    }
  | any;

export const getNeedsAttentionTableColumns = ({
  t,
}: {
  t: TFunction<"translation", undefined>;
}) => [
  createTableColumn({
    columnId: "select",
    renderHeaderCell: (item: IHeaderItem) => {
      if (item.selectionMode === "multiselect") {
        return (
          <TableSelectionCell
            aria-sort="none"
            data-name="select"
            checked={
              item.allRowsSelected
                ? true
                : item.someRowsSelected
                ? "mixed"
                : false
            }
            onClick={(e) => {
              if (item.selectionMode === "multiselect") {
                item.toggleAllRows(e);
                item.updateAllState({
                  expanded: false,
                });
              }
            }}
            onKeyDown={item.toggleAllKeydown}
            checkboxIndicator={{ "aria-label": "Select all rows " }}
          />
        );
      }
      if (item.selectionMode === "single") {
        return <TableSelectionCell type="radio" invisible />;
      }
      return <></>;
    },
    renderCell: (item: EmployeeRowItem) => {
      if (!item.selectionMode) return <></>;
      if (item.selectionMode === "multiselect")
        return (
          <TableSelectionCell
            onClick={() => {
              item.updateState({
                ...item,
                expanded: item.selected ? true : false,
              });
            }}
            checked={item.selected}
            checkboxIndicator={{ "aria-label": "Select row" }}
          />
        );
      if (item.selectionMode === "single") {
        return (
          <TableSelectionCell
            checked={item.selected}
            type="radio"
            radioIndicator={{ "aria-label": "Select row" }}
          />
        );
      }
    },

    compare: (a, b) => {
      return (a?.selected ? 1 : 0) - (b?.selected ? 1 : 0);
    },
  }),

  createTableColumn({
    columnId: "employees",
    renderHeaderCell: () => <>{t("hbh.name.label")}</>,
    renderCell: (item: EmployeeRowItem) => {
      return <EmployeeWithDependentsCell item={item} />;
    },

    compare: (a, b) => {
      const fullNameA = `${a?.firstName} ${a?.lastName}`;
      const fullNameB = `${b?.firstName} ${b?.lastName}`;
      return (fullNameA || "").localeCompare(fullNameB || "");
    },
  }),
  createTableColumn({
    columnId: "email",
    renderHeaderCell: () => <>{t("hbh.email.label")}</>,
    renderCell: (item: EmployeeRowItem) => {
      const email = item?.emailAddress || item?.employeeLoginEmailAddress;
      return <TableCellLayout truncate>{email}</TableCellLayout>;
    },

    compare: (a, b) => {
      return (a?.emailAddress || "").localeCompare(b?.emailAddress || "");
    },
  }),
  createTableColumn({
    columnId: "company",
    renderHeaderCell: () => <>{t("hbh.company.label")}</>,
    renderCell: (item: EmployeeRowItem) => {
      return (
        <TableCellLayout truncate>{(item as any).clientName}</TableCellLayout>
      );
    },

    compare: (a: any, b: any) => {
      const fullNameA = a?.clientName + "";
      const fullNameB = b?.clientName + "";
      return (fullNameA || "").localeCompare(fullNameB || "");
    },
  }),

  createTableColumn({
    columnId: "actionsCell",
    renderHeaderCell: () => <>{t("hbh.actions.label")}</>,
    renderCell: (item: EmployeeRowItem) => {
      return (
        <EmployeeActions
          employeeData={item}
          showResendInvitation={false}
          showDeleteOption={false}
          showManagePackageEligibilityOption={false}
        />
      );
    },

    // compare: (a, b) => {
    //   return a.enrollmentProcessNeeded - b.enrollmentProcessNeeded;
    // },
  }),
];

export const defaultColumnSizingOptions: TableColumnSizingOptions = {
  select: {
    idealWidth: 40,
    minWidth: 40,
    defaultWidth: 40,
    autoFitColumns: false,
  },
  employees: {
    idealWidth: 600,
    minWidth: 400,
    defaultWidth: 600,
  },
  email: {
    idealWidth: 400,
    minWidth: 250,
    defaultWidth: 400,
  },
  company: {
    idealWidth: 400,
    minWidth: 250,
    defaultWidth: 400,
  },

  actionsCell: {
    minWidth: 80,
    idealWidth: 80,
    defaultWidth: 80,
    autoFitColumns: false,
  },
};
